import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Tabs,
  Tab,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import BuildIcon from "@mui/icons-material/Build";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ReusableFormDialog from "./FormModal/ReusableFormDialog";
import DeleteConfirmationDialog from "./FormModal/DeleteConfirmationDialog";
import ReusableDataGrid from "../Components/ReusableDataGrid";
import { serviceValidationSchema } from "./FormModal/validationSchemas";
import {
  buildServicePayload,
  createServiceInitialValues,
  getServiceFormFields,
  prepareServiceFormValues,
} from "./Services/serviceFormConfig";

function Services() {
  const navigate = useNavigate();
  const currentUrl = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [perms, setPerms] = useState({});
  const [currentItem, setCurrentItem] = useState(() =>
    createServiceInitialValues(Helpers.authParentId),
  );

  const getServicePriority = (service) => {
    const priority = Number(service?.priority);
    return Number.isFinite(priority) && priority > 0
      ? priority
      : Number.MAX_SAFE_INTEGER;
  };

  const sortServices = useCallback(
    (items = []) =>
      [...items].sort(
        (a, b) =>
          getServicePriority(a) - getServicePriority(b) ||
          String(a?.service_name || "").localeCompare(
            String(b?.service_name || ""),
            undefined,
            { sensitivity: "base" },
          ),
      ),
    [],
  );

  const getServices = useCallback(() => {
    setLoading(true);
    axios
      .get(`${Helpers.baseUrl}/services/all/${Helpers.authParentId}`, Helpers.headers)
      .then((response) => {
        setServices(
          sortServices(Array.isArray(response.data) ? response.data : []),
        );
      })
      .catch((error) => Helpers.unauthenticated(error, navigate))
      .finally(() => setLoading(false));
  }, [navigate, sortServices]);

  const getPerms = useCallback(() => {
    const link = currentUrl.pathname;
    const permissions = JSON.parse(localStorage.getItem("permissions"));
    if (permissions) {
      const perm = permissions.find((permission) => permission.tab_link === link);
      setPerms(perm || {});
    }
  }, [currentUrl.pathname]);

  useEffect(() => {
    getServices();
    getPerms();
  }, [getPerms, getServices]);

  const canCreate = Helpers.authUser.user_role == null || perms.can_create === 1;
  const canUpdate = Helpers.authUser.user_role == null || perms.can_update === 1;
  const canDelete = Helpers.authUser.user_role == null || perms.can_delete === 1;

  const standardServices = services.filter(
    (service) => (service.serviceType || "Standard") !== "Group",
  );
  const groupServices = services.filter(
    (service) => (service.serviceType || "Standard") === "Group",
  );

  const visibleServices = useMemo(() => {
    const source = activeTab === 0 ? standardServices : groupServices;
    const term = search.trim().toLowerCase();
    if (!term) return source;
    return source.filter((service) => {
      return (
        String(service.service_name || "").toLowerCase().includes(term) ||
        String(service.description || "").toLowerCase().includes(term) ||
        String(service.price || "").toLowerCase().includes(term) ||
        String(service.priority || "").toLowerCase().includes(term) ||
        String(service.tax || "").toLowerCase().includes(term) ||
        String(service.groupCategory || "").toLowerCase().includes(term)
      );
    });
  }, [activeTab, groupServices, search, standardServices]);

  const rows = visibleServices.map((service, index) => ({
    ...service,
    id: service._id,
    serial: index + 1,
  }));

  const baseColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      sortable: false,
      filterable: false,
    },
    {
      field: "service_name",
      headerName: "Service Name",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.4,
      minWidth: 240,
    },
    {
      field: "price",
      headerName: "Price",
      width: 120,
      renderCell: (params) => `$ ${Number(params.row.price || 0).toFixed(2)}`,
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 110,
      renderCell: (params) => params.row.priority || "-",
    },
    {
      field: "tax",
      headerName: "Tax Status",
      width: 130,
    },
  ];

  const columns = [
    ...(activeTab === 0
      ? baseColumns
      : [
          baseColumns[0],
          baseColumns[1],
          {
            field: "groupCategory",
            headerName: "Category",
            width: 160,
            renderCell: (params) => params.row.groupCategory || "-",
          },
          baseColumns[2],
          baseColumns[3],
          baseColumns[4],
          baseColumns[5],
        ]),
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            gap: 1,
          }}
        >
          {canUpdate && (
            <IconButton
              onClick={() => {
                setIsEditing(true);
                setCurrentItem(
                  prepareServiceFormValues(params.row, Helpers.authParentId),
                );
                setOpenFormDialog(true);
              }}
              color="success"
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              onClick={() => {
                setItemToDelete(params.row);
                setOpenDeleteDialog(true);
              }}
              color="error"
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const activeTitle =
    (currentItem?.serviceType || (activeTab === 0 ? "Standard" : "Group")) ===
    "Group"
      ? "Group Service"
      : "Service";

  const handleAddNew = () => {
    setIsEditing(false);
    setCurrentItem(
      createServiceInitialValues(
        Helpers.authParentId,
        activeTab === 0 ? "Standard" : "Group",
      ),
    );
    setOpenFormDialog(true);
  };

  const handleSave = (values) => {
    setBtnLoading(true);
    axios
      .post(
        `${Helpers.baseUrl}/services/${isEditing ? "update" : "add"}`,
        buildServicePayload(values, Helpers.authParentId),
        Helpers.headers,
      )
      .then(() => {
        getServices();
        setCurrentItem(createServiceInitialValues(Helpers.authParentId));
        setIsEditing(false);
        setOpenFormDialog(false);
        Helpers.toast("success", "Service saved successfully");
      })
      .catch((error) => Helpers.toast("error", Helpers.error_response(error)))
      .finally(() => setBtnLoading(false));
  };

  const confirmDelete = () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    axios
      .get(`${Helpers.baseUrl}/services/delete/${itemToDelete._id}`, Helpers.headers)
      .then(() => {
        getServices();
        setOpenDeleteDialog(false);
        setItemToDelete(null);
        Helpers.toast("success", "Service deleted successfully");
      })
      .catch((error) => Helpers.unauthenticated(error, navigate))
      .finally(() => setDeleteLoading(false));
  };

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: "64px",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #2563eb 55%, #0891b2 100%)",
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: 300,
              height: 300,
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.7)" }} />
            }
            sx={{ mb: 2 }}
          >
            <MuiLink
              component={Link}
              to="/user/dashboard"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "rgba(255,255,255,0.9)",
                fontSize: "0.875rem",
                "&:hover": { color: "white" },
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Home
            </MuiLink>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              Services
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: "flex", alignItems: "center", position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                mr: 2,
              }}
            >
              <img
                src={require("../images/icons/services.png")}
                alt="Services"
                style={{ height: 40, width: 40, objectFit: "contain" }}
              />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>
                Services
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                Manage standard services and customer-facing group services
              </Typography>
            </Box>
            <Chip
              label={`${rows.length} ${rows.length === 1 ? "item" : "items"}`}
              size="small"
              sx={{
                ml: "auto",
                background: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                color: "white",
                fontWeight: 600,
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            />
          </Box>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              background: "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Tabs
                value={activeTab}
                onChange={(_, value) => {
                  setActiveTab(value);
                  setSearch("");
                }}
                sx={{ minHeight: 40 }}
              >
                <Tab icon={<BuildIcon />} iconPosition="start" label="Standard Services" />
                <Tab icon={<ViewModuleIcon />} iconPosition="start" label="Group Services" />
              </Tabs>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ minWidth: { md: "45%" } }}>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                {canCreate && (
                  <IconButton
                    color="primary"
                    onClick={handleAddNew}
                    sx={{
                      borderRadius: 1.5,
                      px: 1.5,
                      width: { xs: "100%", sm: "auto" },
                      border: "1px solid",
                      borderColor: "primary.main",
                    }}
                  >
                    <AddIcon fontSize="small" />
                    <Typography sx={{ ml: 0.75, fontSize: 14 }}>
                      Add {activeTab === 0 ? "Service" : "Group Service"}
                    </Typography>
                  </IconButton>
                )}
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <ReusableDataGrid
              rows={rows}
              loading={loading}
              columns={columns}
              pageSize={10}
              pageSizeOptions={[10, 25, 50, 100]}
              noRowsMessage={`No ${activeTab === 0 ? "services" : "group services"} available to display`}
              autoHeight={false}
              sx={{
                flex: 1,
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.04)",
                },
              }}
            />
          </Box>
        </Paper>
      </Container>

      <ReusableFormDialog
        open={openFormDialog}
        onClose={() => {
          setOpenFormDialog(false);
          setCurrentItem(createServiceInitialValues(Helpers.authParentId));
          setIsEditing(false);
        }}
        title={isEditing ? `Edit ${activeTitle}: ${currentItem.service_name || ""}` : `Add New ${activeTitle}`}
        initialValues={currentItem}
        validationSchema={serviceValidationSchema}
        fields={(values) => getServiceFormFields(values)}
        onSubmit={handleSave}
        loading={btnLoading}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDelete}
        itemName={itemToDelete?.service_name}
        loading={deleteLoading}
        warningText="This action cannot be undone. Existing receipts will keep their saved lines, but new receipts will no longer be able to use this service."
      />
    </Box>
  );
}

export default Services;
